import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { createPortal } from "react-dom";

import { Button, Icons, Tabs, useCopy } from "../../../../../ds";
import { CODE_HOST_ATTRIBUTE, CodeBlock, PreparedBody, TABS_HOST_ATTRIBUTE, TabSet, prepareBody } from "./readerBody";

interface Hosts {
    key: string;
    tabs: (Element | null)[];
    code: (Element | null)[];
}

/** One `::: tabs` block as the library `Tabs`; each panel is rendered like the body (it may hold code, images, more tabs). */
function BodyTabs(props: { set: TabSet }) {
    const [value, setValue] = useState("0");
    const items = useMemo(() => props.set.labels.map((label, i) => ({ value: String(i), label })), [props.set]);
    return (
        <Tabs className="ark-prose-tabs" aria-label={props.set.labels.join(", ")} items={items} value={value} onChange={setValue}>
            <RenderedHtml html={props.set.panels[Number(value)] ?? ""} />
        </Tabs>
    );
}

/** The head of a fenced code block: its language and a Copy button. */
function CodeHead(props: { block: CodeBlock }) {
    const [state, copy] = useCopy(props.block.text);
    return (
        <>
            <span className="ark-code-lang">{props.block.language ?? "Code"}</span>
            <Button
                type="white"
                size="sm"
                icon={state === "copied" ? <Icons.check size={14} /> : <Icons.copy size={14} />}
                text={state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : "Copy"}
                aria-label={state === "copied" ? "Code copied" : "Copy code"}
                onClick={copy}
            />
        </>
    );
}

export interface PreparedHtmlProps {
    body: PreparedBody;
    className?: string;
    innerRef?: RefObject<HTMLDivElement>;
}

/**
 * A prepared body: the sanitized HTML in the DOM, with the library `Tabs` and the code Copy buttons portalled into their
 * placeholders. React never rewrites the HTML while `body.html` stays the same, so the portals keep their hosts.
 */
export function PreparedHtml(props: PreparedHtmlProps) {
    const { body } = props;
    const ownRef = useRef<HTMLDivElement>(null);
    const ref = props.innerRef ?? ownRef;
    const [hosts, setHosts] = useState<Hosts>();

    useLayoutEffect(() => {
        const root = ref.current;
        if (!root) return;
        setHosts({
            key: body.key,
            tabs: body.tabs.map((_, i) => root.querySelector(`[${TABS_HOST_ATTRIBUTE}="${body.key}-${i}"]`)),
            code: body.code.map((_, i) => root.querySelector(`[${CODE_HOST_ATTRIBUTE}="${body.key}-${i}"]`)),
        });
    }, [body, ref]);

    const live = hosts?.key === body.key ? hosts : undefined;
    return (
        <>
            <div ref={ref} className={props.className} dangerouslySetInnerHTML={{ __html: body.html }} />
            {live?.tabs.map((host, i) => (host ? createPortal(<BodyTabs set={body.tabs[i]} />, host, `${body.key}-t${i}`) : null))}
            {live?.code.map((host, i) => (host ? createPortal(<CodeHead block={body.code[i]} />, host, `${body.key}-c${i}`) : null))}
        </>
    );
}

/** Sanitized HTML → prepared body (heading ids, tabs, code heads) → DOM. */
export function RenderedHtml(props: { html: string; className?: string }) {
    const body = useMemo(() => prepareBody(props.html), [props.html]);
    return <PreparedHtml body={body} className={props.className} />;
}
